/**
 * A New case's step (5 Oct), opened by its owner — Backoffice, the manager —
 * on its own form, prefilled with the new hire and what HR said (roles, note,
 * reference user, dates), to complete and submit. Filing it writes the
 * step's marker on the New User Request, so it is never filed twice.
 *
 * Business Systems Access (5 Oct, product owner): Backoffice picks the
 * particulars HR no longer does — 365FO areas, Bluestar areas with View or
 * Design, Power BI models — and sees live who approves each, from the
 * approval matrix, for the company chosen on the form. The picks go into
 * "Roles needed" on submit, and the approvers are asked on the request.
 *
 * Filed, it returns to the access request, not the new ticket (5 Oct,
 * product owner's UAT: the steps are raised one after another from there).
 * A toast names the new number; the case's queries are refreshed by the
 * mutation, so the item reads as submitted.
 */

import { Alert, Badge, Button, Typography } from "@norican/react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { AppBreadcrumbs, ErrorState, LoadingState, PageContainer } from "@/components/common";
import { ServiceRequestForm, useFeedbackToast } from "@/features/tickets";
import { useDocumentTitle } from "@/hooks";
import { useLanguage, useT } from "@/i18n";
import type { AccessApprovalNeed, AccessPrefillResponse } from "@/lib/api";

import {
  useAccessItemPrefill,
  useApproversPreview,
  useFileAccessItem,
  useReferenceAccess,
} from "./api";
import { useAccessHome } from "./components/access-home";
import { NeedRule, useNeedStatus } from "./components/approval-needs";
import { ReferenceDiffView, ReferenceItemPanel } from "./components/reference-access";
import { RolePicker } from "./components/role-picker";
import {
  accessCasePath,
  areasWithoutSubroles,
  bluestarPickerOnForm,
  needStateTone,
  prefilledNoteKey,
  type RoleSelection,
  rolesForSubmission,
  toSubmission,
} from "./lib/access";
import {
  applyReference,
  EMPTY_CHOICE,
  entriesOf,
  hasDiff,
  type ReferenceChoice,
  referenceDiff,
  systemsForItem,
} from "./lib/reference";

export function AccessItemPage() {
  const { caseId = "", n = "0" } = useParams<{ caseId: string; n: string }>();
  const index = Number.parseInt(n, 10) || 0;
  const { t } = useT("tickets");
  useDocumentTitle(t("access.openPrefilled"));
  const prefill = useAccessItemPrefill(caseId, index);
  const back = accessCasePath(caseId);
  const home = useAccessHome();
  const breadcrumbs = (
    <AppBreadcrumbs
      items={[
        { label: home.label, to: home.to },
        { label: t("access.caseLink"), to: back },
        { label: t("access.openPrefilled") },
      ]}
    />
  );
  return (
    <PageContainer title={t("access.openPrefilled")} breadcrumbs={breadcrumbs}>
      <div className="flex flex-col gap-4">
        {prefill.isPending ? (
          <LoadingState label={t("access.wizard.forms.loading")} />
        ) : prefill.isError ? (
          <ErrorState
            error={prefill.error}
            actions={
              <Button variant="outline" tone="neutral" render={<Link to={back} />}>
                {t("access.backToCase")}
              </Button>
            }
          />
        ) : (
          <StepForm caseId={caseId} n={index} prefill={prefill.data} back={back} />
        )}
      </div>
    </PageContainer>
  );
}

interface StepFormProps {
  caseId: string;
  n: number;
  prefill: AccessPrefillResponse;
  back: string;
}

/** The step's form, with the owner's role picker and its approvers when the item has them. */
function StepForm({ caseId, n, prefill, back }: StepFormProps) {
  const { t } = useT("tickets");
  const { language } = useLanguage();
  const navigate = useNavigate();
  const toast = useFeedbackToast();
  const file = useFileAccessItem(caseId, n);
  const list = prefill.owner_roles ?? null;
  const [roles, setRoles] = useState<RoleSelection>(() => ({
    choices: (prefill.roles ?? []).map((choice) => ({
      id: choice.id,
      level: choice.level ?? null,
      pre_approved: choice.pre_approved ?? false,
      subroles: choice.subroles ?? [],
    })),
    other: "",
  }));
  // 2 Nov (Helle, Rusty): each ticked 365FO area needs at least one sub-role.
  const [showBare, setShowBare] = useState(false);
  const bare = list !== null ? areasWithoutSubroles(list, roles) : [];
  const [company, setCompany] = useState<{ value: string | null; valueIds: string[] }>({
    value: null,
    valueIds: [],
  });
  const [area, setArea] = useState<{ value: string | null; valueIds: string[] }>({
    value: null,
    valueIds: [],
  });
  // 21 Oct: the reference user's entries for this item, ticked as the picker
  // holds them (the requester's picks start it), so Backoffice can keep,
  // remove or add and finish the selection here.
  const referenceId = prefill.reference_import ? (prefill.reference_user_id ?? null) : null;
  const reference = useReferenceAccess(referenceId, referenceId !== null);
  const referenceSystems =
    reference.data?.enabled === true
      ? systemsForItem(reference.data.systems ?? [], prefill.item)
      : [];
  const chosen = prefill.reference_selection ?? null;
  const [choice, setChoice] = useState<ReferenceChoice | null>(null);
  // The requester's own kept entries (by their words), once the entries are here.
  const startChoice: ReferenceChoice =
    choice ??
    (chosen !== null
      ? {
          applied: true,
          kept: entriesOf(referenceSystems)
            .filter((item) => !item.role && (chosen.kept ?? []).includes(item.label))
            .map((item) => item.id),
          added: [],
        }
      : EMPTY_CHOICE);
  const diff = referenceDiff(
    referenceSystems,
    roles,
    startChoice,
    list ?? undefined,
    t("access.reference.generic"),
  );
  const picks = rolesForSubmission(roles);
  // 14 Oct: CRM has no picks; its approver is the matrix's by CRM area and
  // region, so the preview follows the area and the company on the form.
  const byArea = prefill.area_parameter_id != null;
  const preview = useApproversPreview(
    caseId,
    n,
    {
      roles: picks,
      company_value_ids: company.valueIds,
      company_value: company.value,
      area_value_ids: area.valueIds,
      area_value: area.value,
    },
    prefill.names_approvers === true && (picks.length > 0 || byArea),
  );
  return (
    <>
      <Alert tone="info">
        {prefill.case_type === "leave"
          ? t("access.openPrefilledBannerLeave")
          : t("access.openPrefilledBanner")}
      </Alert>
      {prefill.approver_field_label ? (
        <Alert tone="info">
          {t("access.approvals.formApprover", { field: prefill.approver_field_label })}
        </Alert>
      ) : null}
      {chosen !== null &&
      hasDiff({
        kept: chosen.kept ?? [],
        removed: chosen.removed ?? [],
        added: chosen.added ?? [],
      }) ? (
        <section
          aria-label={t("access.reference.selection.title", {
            name: prefill.reference_user_name ?? "",
          })}
          className="border-border-subtle flex flex-col gap-2 rounded-md border p-3"
        >
          <Typography as="h2" variant="body-sm" className="font-medium">
            {t("access.reference.selection.title", { name: prefill.reference_user_name ?? "" })}
          </Typography>
          <ReferenceDiffView
            diff={{
              kept: chosen.kept ?? [],
              removed: chosen.removed ?? [],
              added: chosen.added ?? [],
            }}
          />
        </section>
      ) : null}
      {referenceId !== null && referenceSystems.length > 0 ? (
        <ReferenceItemPanel
          name={`access-owner-reference-${prefill.item}`}
          personName={prefill.reference_user_name ?? reference.data?.person_name ?? ""}
          systems={referenceSystems}
          selection={roles}
          choice={startChoice}
          diff={diff}
          pickerBelow={list !== null}
          onChange={(next) => {
            setRoles(next.selection);
            setChoice(next.choice);
          }}
          onApply={() => {
            const next = applyReference(referenceSystems, roles, startChoice);
            setRoles(next.selection);
            setChoice(next.choice);
          }}
        />
      ) : null}
      {list !== null ? (
        <RolePicker
          name={`access-owner-roles-${prefill.item}`}
          list={list}
          value={roles}
          onChange={setRoles}
          showOther={false}
          askPreApproval
          hint={t("access.approvals.pickerHint")}
          error={
            showBare && bare.length > 0
              ? t("access.approvals.subrolesRequired", { areas: bare.join(", ") })
              : undefined
          }
        />
      ) : null}
      {prefill.names_approvers ? (
        <section
          aria-label={t("access.approvals.previewTitle")}
          className="border-border-subtle flex flex-col gap-2 rounded-md border p-3"
        >
          <Typography as="h2" variant="body-sm" className="font-medium">
            {t("access.approvals.previewTitle")}
          </Typography>
          {picks.length === 0 && !byArea ? (
            <Typography as="p" variant="caption" tone="muted">
              {t("access.approvals.previewNoPicks")}
            </Typography>
          ) : preview.isError ? (
            <ErrorState variant="inline" error={preview.error} />
          ) : preview.data === undefined ? (
            <LoadingState inline />
          ) : (
            <ApproverPreview
              companies={preview.data.companies ?? []}
              needs={preview.data.needs ?? []}
            />
          )}
        </section>
      ) : null}
      <ServiceRequestForm
        starterPrefill={{
          offeringId: prefill.offering_id,
          subject: prefill.subject,
          description: prefill.description ?? undefined,
          values: prefill.values ?? [],
          prefilledNote: t(prefilledNoteKey(prefill.case_type)),
        }}
        collect={{
          submitLabel: t("create.submitRequest"),
          onCancel: () => void navigate(back),
          pending: file.isPending,
          error: file.error,
          watchParameterId: prefill.company_parameter_id ?? null,
          watchAlsoParameterId: prefill.area_parameter_id ?? null,
          onWatchAlsoChange: setArea,
          // 14 Oct: the new forms' fields only the HelpDesk writes.
          hiddenParameterIds: prefill.held_parameter_ids ?? [],
          // 28 Oct: the old form's required questions (CRM V11's).
          requiredParameterIds: prefill.required_parameter_ids ?? [],
          onWatchChange: setCompany,
          // 8 Oct: the owner's picker above is the Bluestar one; the form's
          // "Roles or access needed" stays the free text it adds to.
          bluestarAreas: bluestarPickerOnForm(list, prefill.case_type),
          onCollect: (form) => {
            if (bare.length > 0) {
              setShowBare(true);
              return;
            }
            void file.mutateAsync(toSubmission(prefill.item, form, null, language, picks)).then(
              (filed) => {
                // 13 Oct (new forms): an item that needs approval is kept in the
                // HelpDesk and filed once approved, so it has no number yet.
                toast.success(
                  filed.held
                    ? t("access.approvals.heldUntilApproved")
                    : t("create.success", { number: filed.ticket_number }),
                  filed.note_failed ? t("access.approvals.noteFailed") : undefined,
                );
                return navigate(back);
              },
              () => undefined,
            );
          },
        }}
      />
    </>
  );
}

/** "Approver: <name> (rule: Finance for IN)" per pick, for the company on the form. */
function ApproverPreview({
  companies,
  needs,
}: {
  companies: readonly string[];
  needs: readonly AccessApprovalNeed[];
}) {
  const { t } = useT("tickets");
  const status = useNeedStatus();
  return (
    <>
      <Typography as="p" variant="caption" tone="muted">
        {companies.length > 0
          ? t("access.approvals.previewCompanies", { companies: companies.join(", ") })
          : t("access.approvals.previewNoCompany")}
      </Typography>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {needs.map((need) => (
          <li key={need.need_id} className="flex min-w-0 flex-col gap-1">
            <span className="flex min-w-0 flex-wrap items-center gap-2">
              <Typography as="span" variant="body-sm" className="min-w-0 break-words">
                {need.what}
              </Typography>
              {need.state !== "waiting" ? (
                <Badge tone={needStateTone(need.state)} variant="subtle" size="xs">
                  {status(need)}
                </Badge>
              ) : null}
            </span>
            <NeedRule need={need} />
          </li>
        ))}
      </ul>
    </>
  );
}
